import { Table, TableBody, TableContainer, TableRow, useMediaQuery, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import defaultProps from '../../../config/defaultProps'
import {
  getCurrentUser,
  getIsAdmin,
  getIsEmployee,
  getIsLeader,
  getIsManagement,
} from '../../../store/redux/reducers/currentUserSlice'
import { getSelectedDate } from '../../../store/redux/reducers/monthPickerSlice'
import { useGetUsersQuery } from '../../../store/redux/reducers/usersSlice'
import { useGetVacationAcknowledgementsBulkQuery } from '../../../store/redux/reducers/vacationAcknowledgementApiSlice'
import { RootState } from '../../../store/redux/store'
import { defaultSelectedVacationData, SelectedDataState } from '../../../store/types/components/table/vacationTable'
import { EmployeeVacation, VacationRequestProps } from '../../../store/types/utils/VacationTypes'
import { calculateTotalVacationCount, calculateVacationCount } from '../../../store/utils/vacationUtils'
import AddVacationDialog from '../../../views/VacationView/AddVacationDialog'
import ChoicePopup from '../../../views/VacationView/ChoicePopup'
import DeleteVacationDialog from '../../../views/VacationView/DeleteVacationDialog'
import MobileVacationSummary from '../../../views/VacationView/MobileVacationSummary'
import VacationDialog from '../../../views/VacationView/VacationDialog'
import ViewVacationDialog from '../../../views/VacationView/ViewVacationDialog'
import VacationTableFooter from './VacationTableFooter'
import VacationTableHeader from './VacationTableHeader'
import VacationTableRow from './VacationTableRow'

interface VacationTableProps {
  empData: EmployeeVacation[]
  isFiltersOpen: boolean
  chosenDate: string | null
  setChosenDate: (date: string | null) => void
  dialogReference: React.RefObject<HTMLDivElement>
  getEmployeeRequestProps: VacationRequestProps
}

const VacationTable = React.memo<VacationTableProps>(
  ({ empData, isFiltersOpen, chosenDate, setChosenDate, dialogReference, getEmployeeRequestProps }) => {
    const [selectedData, setSelectedData] = useState<SelectedDataState>(defaultSelectedVacationData)
    const { i18n } = useTranslation()
    const selectedDateFromState = useSelector((state: RootState) => getSelectedDate(state))
    const currentUser = useSelector(getCurrentUser)
    const selectedDate = useMemo(() => {
      dayjs.locale(i18n.language)
      return dayjs(selectedDateFromState)
    }, [selectedDateFromState, i18n.language])
    const roles = currentUser?.roles
    const isEmployee = useSelector(getIsEmployee)
    const isManagement = useSelector(getIsManagement)
    const isAdmin = useSelector(getIsAdmin)

    const {
      data: usersResponse,
      isSuccess,
      refetch,
    } = useGetUsersQuery(selectedData.requestProps, { skip: isEmployee })
    const { data: activeUsersResponse } = useGetUsersQuery(
      { ...defaultProps, employeeStatus: 'ACTIVE' },
      { skip: isEmployee }
    )
    const theme = useTheme()
    const [isOpenConfirm, setIsOpenConfirm] = useState(false)
    const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
    const isLeader = useSelector(getIsLeader)

    const allVacationIds = useMemo(() => {
      const ids: string[] = []
      empData.forEach(emp => {
        emp.vacation.forEach(v => ids.push(v.vacationId))
      })
      return Array.from(new Set(ids))
    }, [empData])

    const { data: acknowledgementData, isFetching: isFetchingAcknowledgements } =
      useGetVacationAcknowledgementsBulkQuery(allVacationIds, {
        skip: !(isLeader || isManagement || isAdmin) || !allVacationIds.length,
      })

    const employeeLeadersMap = useMemo(() => {
      const map: Record<string, string[]> = {}
      selectedData.users.forEach(user => {
        map[user.id] = user.leaders.map(leader => leader.id)
      })
      return map
    }, [selectedData.users])

    useEffect(() => {
      if (activeUsersResponse) {
        setSelectedData(prevState => ({
          ...prevState,
          usersCount: activeUsersResponse.totalElements,
        }))
      }
    }, [activeUsersResponse])

    useEffect(() => {
      if (!isEmployee) refetch()
    }, [selectedData.requestProps, refetch, roles, isEmployee])

    useEffect(() => {
      if (isSuccess) {
        setSelectedData(prevState => ({
          ...prevState,
          users: usersResponse.content,
        }))
      }
    }, [isSuccess, usersResponse])

    const setEditDialog = useCallback(() => {
      setSelectedData(prevState => ({
        ...prevState,
        isSecondDialogOpen: true,
      }))
    }, [])

    const handleVacationClick = useCallback(
      (
          employee: EmployeeVacation,
          endDate: string,
          startDate: string,
          desc: string,
          vacationId: string,
          displayStart: string,
          displayEnd: string,
          createdAt: string,
          groupIds: string[]
        ) =>
        () => {
          setSelectedData(prevState => ({
            ...prevState,
            isViewDialogOpen: true,
            secondSelectedEmployee: employee,
            secondSelectedDateCell: startDate,
            secondEndDateValue: endDate,
            desc,
            vacationId,
            vacationIds: groupIds,
            displayStart,
            displayEnd,
            createdAt,
          }))
        },
      []
    )

    const nonVacClick = useCallback(
      (employee: EmployeeVacation, date: string) => (_: React.MouseEvent<HTMLTableCellElement>) => {
        setSelectedData(prevState => ({
          ...prevState,
          isDialogOpen: true,
          selectedEmployee: employee,
          selectedDateCell: date,
          startDate: date,
          endDateValue: date,
        }))
      },
      []
    )

    const handleClose = useCallback(() => {
      setSelectedData(prevState => ({
        ...prevState,
        isDialogOpen: false,
        selectedEmployee: null,
        selectedDateCell: null,
        endDateValue: null,
        startDate: null,
        desc: '',
        vacationId: null,
        vacationIds: [],
        displayEnd: null,
        displayStart: null,
      }))
    }, [])

    const handleSecondClose = useCallback(() => {
      setSelectedData(prevState => ({
        ...prevState,
        isSecondDialogOpen: false,
        secondSelectedEmployee: null,
        secondSelectedDateCell: null,
        secondEndDateValue: null,
        desc: null,
        vacationId: null,
        vacationIds: [],
        displayStart: null,
        displayEnd: null,
      }))
    }, [])

    const handleChoiceClose = useCallback(() => {
      setSelectedData(prevState => ({
        ...prevState,
        choiceAnchorEl: null,
      }))
    }, [])

    const handleViewClose = useCallback(() => {
      setSelectedData(prevState => ({
        ...prevState,
        isViewDialogOpen: false,
      }))
    }, [])

    const setChoiceAnchor = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
      setSelectedData(prevState => ({
        ...prevState,
        choiceAnchorEl: event.currentTarget,
      }))
    }, [])

    const handleDateSelectorClick = (day: string) => {
      if (day === chosenDate) {
        setChosenDate(null)
      } else {
        setChosenDate(day)
      }
    }

    const closeConfirmationDialog = () => {
      setIsOpenConfirm(false)
      setSelectedData(prevState => ({ ...prevState, choiceAnchorEl: null }))
    }

    const cellRefs = useRef<React.RefObject<HTMLTableCellElement>[][]>(
      Array.from({ length: empData.length }, () =>
        Array.from({ length: selectedDate.daysInMonth() }, () => React.createRef<HTMLTableCellElement>())
      )
    )

    const getCell = (row: number, col: number) => {
      return cellRefs.current[row][col].current
    }

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLTableCellElement>) => {
        let currentRow = 0
        let currentCol = 0
        const totalRows = empData.length
        const totalCols = selectedDate.daysInMonth()

        if (event.currentTarget.id) {
          const [rowStr, colStr] = event.currentTarget.id.split('-')
          currentRow = parseInt(rowStr, 10)
          currentCol = parseInt(colStr, 10)
        }

        const moveFocus = (rowDelta: number, colDelta: number) => {
          currentRow = (currentRow + rowDelta + totalRows) % totalRows
          currentCol = (currentCol + colDelta + totalCols) % totalCols
          while (getCell(currentRow, currentCol) === null) currentCol = (currentCol + colDelta + totalCols) % totalCols
        }

        switch (event.key) {
          case 'ArrowDown':
            moveFocus(1, 0)
            break
          case 'ArrowUp':
            moveFocus(-1, 0)
            break
          case 'ArrowRight':
            moveFocus(0, 1)
            break
          case 'ArrowLeft':
            moveFocus(0, -1)
            break
          case 'Enter':
            getCell(currentRow, currentCol)?.click()
            return
          default:
            return
        }
        getCell(currentRow, currentCol)?.focus()
      },
      [empData.length, selectedDate]
    )

    const renderTableRows = useMemo(
      () =>
        empData.map((employee, rowIndex) => (
          <TableRow key={rowIndex} sx={{ minHeight: '7vh' }}>
            <VacationTableRow
              chosenDate={chosenDate}
              empData={empData}
              rowIndex={rowIndex}
              selectedDate={selectedDate}
              employee={employee}
              handleClick={handleVacationClick}
              nonVacClick={nonVacClick}
              handleKeyDown={handleKeyDown}
              cellRefs={cellRefs}
              acknowledgementData={acknowledgementData}
              isFetchingAcknowledgements={isFetchingAcknowledgements}
              employeeLeadersMap={employeeLeadersMap}
            />
          </TableRow>
        )),
      [
        empData,
        handleVacationClick,
        nonVacClick,
        selectedDate,
        chosenDate,
        handleKeyDown,
        acknowledgementData,
        isFetchingAcknowledgements,
        employeeLeadersMap,
      ]
    )

    const isSingleRowRender = renderTableRows.length === 1

    useEffect(() => {
      if (isSingleRowRender) {
        setChosenDate(null)
      }
    }, [isSingleRowRender, setChosenDate])

    return (
      <>
        <TableContainer
          sx={{
            height: 'fit-content',
            maxHeight: '75vh',
            flex: 1,
            border: '1px solid',
            borderColor: 'background.elevation8',
          }}
        >
          <Table
            sx={{
              width: '100%',
              minWidth: '1150px',
              maxHeight: '100%',
              tableLayout: 'fixed',
              position: 'relative',
              borderCollapse: 'separate',
            }}
            size="small"
            stickyHeader
          >
            <VacationTableHeader
              chosenDate={chosenDate}
              selectedDate={selectedDate}
              isFiltersOpen={isFiltersOpen}
              isSingleRowRender={isSingleRowRender}
              handleDateSelectorClick={handleDateSelectorClick}
            />
            <TableBody>{renderTableRows}</TableBody>
            <VacationTableFooter
              selectedDate={selectedDate}
              totalVacationCount={calculateTotalVacationCount(selectedDate, empData)}
              calculateVacationCount={date => calculateVacationCount(date, empData)}
            />
          </Table>
        </TableContainer>
        {isDownSm && <MobileVacationSummary totalVacationCount={calculateTotalVacationCount(selectedDate, empData)} />}
        {(selectedData.selectedEmployee?.id === currentUser?.id || (currentUser && isManagement)) && (
          <AddVacationDialog
            ref={dialogReference}
            isOpen={selectedData.isDialogOpen}
            onClose={handleClose}
            selected={selectedData.selectedEmployee}
            selectedCell={selectedData.selectedDateCell}
          />
        )}
        <VacationDialog
          displayEnd={selectedData.displayEnd}
          displayStart={selectedData.displayStart}
          vacationId={selectedData.vacationId}
          selected={selectedData.secondSelectedEmployee}
          isOpen={selectedData.isSecondDialogOpen}
          onClose={handleSecondClose}
          descriptionText={selectedData.desc}
          getEmployeeRequestProps={getEmployeeRequestProps}
        />
        <ViewVacationDialog
          ref={dialogReference}
          displayStart={selectedData.displayStart}
          displayEnd={selectedData.displayEnd}
          currentUser={currentUser}
          onClose={handleViewClose}
          isOpen={selectedData.isViewDialogOpen}
          selectedEmployee={selectedData.secondSelectedEmployee}
          description={selectedData.desc}
          setChoiceAnchor={setChoiceAnchor}
          vacationId={selectedData.vacationId}
          vacationIds={selectedData.vacationIds}
          createdAt={selectedData.createdAt}
        />
        <ChoicePopup
          anchor={selectedData.choiceAnchorEl}
          isOpen={Boolean(selectedData.choiceAnchorEl)}
          onClose={handleChoiceClose}
          openEdit={setEditDialog}
          closeView={handleViewClose}
          setIsOpenConfirm={setIsOpenConfirm}
        />
        <DeleteVacationDialog
          isOpen={isOpenConfirm}
          setIsOpen={setIsOpenConfirm}
          onClose={closeConfirmationDialog}
          displayStart={selectedData.displayStart}
          displayEnd={selectedData.displayEnd}
          vacationId={selectedData.vacationId}
          secondSelectedEmployee={selectedData.secondSelectedEmployee}
        />
      </>
    )
  }
)

export default React.memo(VacationTable)
